import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import './Topbar.css';

const MESSAGES = [
  { icon: '✦', label: 'FREE SHIPPING', sub: 'on orders above ₹20,000' },
  { icon: '✦', label: '7-DAY RETURNS', sub: 'no questions asked' },
  { icon: '✦', label: 'CERTIFIED REFURBISHED', sub: 'grade A quality assured' },
  { icon: '✦', label: 'SAME-DAY DISPATCH', sub: 'order before 2 PM' },
];

const Topbar = () => {
  const rootRef      = useRef(null);
  const trackRef     = useRef(null);
  const lineLeftRef  = useRef(null);
  const lineRightRef = useRef(null);
  const dotRefs      = useRef([]);
  const marqueeRef   = useRef(null);
  const pausedRef    = useRef(false);
  const [paused, setPaused] = useState(false);

  const pauseMarquee  = () => { pausedRef.current = true;  setPaused(true);  };
  const resumeMarquee = () => { pausedRef.current = false; setPaused(false); };

  useEffect(() => {
    const root  = rootRef.current;
    const track = trackRef.current;
    if (!root || !track) return;

    const ctx = gsap.context(() => {

      /* ── 1. ENTRY ── */
      const tl = gsap.timeline({ defaults: { ease: 'expo.out' } });

      tl.fromTo(root,
        { clipPath: 'inset(0 100% 0 0)', opacity: 0 },
        { clipPath: 'inset(0 0% 0 0)',   opacity: 1, duration: 0.9 }
      )
      .fromTo(
        [lineLeftRef.current, lineRightRef.current],
        { scaleX: 0, opacity: 0 },
        { scaleX: 1, opacity: 1, duration: 0.6, stagger: 0.15, ease: 'power3.out' },
        '-=0.4'
      )
      .fromTo(
        dotRefs.current,
        { scale: 0, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.4, stagger: 0.08, ease: 'back.out(3)' },
        '-=0.3'
      );

      /* ── 2. LINE PULSE ── */
      gsap.to([lineLeftRef.current, lineRightRef.current], {
        opacity: 0.3,
        duration: 1.8,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        stagger: 0.4,
      });

      /* ── 3. DOT FLOAT ── */
      dotRefs.current.forEach((dot, i) => {
        if (!dot) return;
        gsap.to(dot, {
          y: -2,
          duration: 1.2 + i * 0.15,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
          delay: i * 0.2,
        });
      });

      /* ── 4. MARQUEE ── */
      const startMarquee = () => {
        const totalW = track.scrollWidth / 3; // divided by 3 since tripled
        marqueeRef.current = gsap.to(track, {
          x:       `-=${totalW}`,
          duration: totalW / 55,
          ease:    'none',
          repeat:  -1,
          modifiers: {
            x: gsap.utils.unitize(x => parseFloat(x) % totalW),
          },
        });
      };

      tl.then(startMarquee);

    }, root);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (!marqueeRef.current) return;
    paused ? marqueeRef.current.pause() : marqueeRef.current.resume();
  }, [paused]);

  /* triple for seamless loop */
  const all = [...MESSAGES, ...MESSAGES, ...MESSAGES];

  return (
    <div className="tb-root" ref={rootRef}>

      <div className="tb-noise" />

      <span className="tb-line tb-line--left"  ref={lineLeftRef}  />
      <span className="tb-line tb-line--right" ref={lineRightRef} />

      {/* ── LEFT CORNER ── */}
      <div className="tb-corner tb-corner--left">
        <span className="tb-corner-dot" ref={el => (dotRefs.current[0] = el)} />
        <span className="tb-corner-label">NEXAWAVE</span>
      </div>

      {/* ── MARQUEE ── */}
      <div
        className="tb-viewport"
        onMouseEnter={pauseMarquee}
        onMouseLeave={resumeMarquee}
        onTouchStart={pauseMarquee}
        onTouchEnd={resumeMarquee}
      >
        <div className="tb-track" ref={trackRef}>
          {all.map((msg, i) => (
            <span key={i} className="tb-item">
              <span className="tb-star">{msg.icon}</span>
              <span className="tb-label">{msg.label}</span>
              <span className="tb-sep">—</span>
              <span className="tb-sub">{msg.sub}</span>
            </span>
          ))}
        </div>
      </div>

      {/* ── RIGHT CORNER ── */}
      <div className="tb-corner tb-corner--right">
        <span className="tb-corner-label">STORE</span>
        <span className="tb-corner-dot" ref={el => (dotRefs.current[1] = el)} />
      </div>

    </div>
  );
};

export default Topbar;